/**
 * useFeatureFlags - React Hook for functional components
 * 
 * This hook provides feature flag functionality to functional components
 * with automatic updates when flags change.
 * 
 * Usage:
 * 
 * import { useFeatureFlags } from './useFeatureFlags';
 * 
 * const MyComponent = () => {
 *   const { isFeatureEnabled, areFeaturesEnabled } = useFeatureFlags(['my-flag', 'another-flag']);
 * 
 *   return (
 *     <div>
 *       {isFeatureEnabled('my-flag') && <Feature />}
 *     </div>
 *   );
 * };
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribeToFlagChanges, isFeatureEnabled, areFeaturesEnabled, getFeatureValue } from './FeatureToggleManager';

/**
 * Hook for feature flag functionality in functional components
 * @param {string[]} watchedFlags - Array of flag keys to watch for changes (optional)
 * @returns {object} Object with flag checking functions and update counter
 */
const useFeatureFlags = (watchedFlags = null) => {
  const [flagUpdateCounter, setFlagUpdateCounter] = useState(0);
  const unsubscribeRef = useRef(null);
  const watchedFlagsRef = useRef(watchedFlags);

  // Update ref when watchedFlags changes
  useEffect(() => {
    watchedFlagsRef.current = watchedFlags;
  }, [watchedFlags]);

  // Memoized flag checking functions to prevent unnecessary re-renders
  const isFeatureEnabledMemo = useCallback((flagKey) => {
    return isFeatureEnabled(flagKey);
  }, [flagUpdateCounter]); // Re-memoize when flags update

  const areFeaturesEnabledMemo = useCallback((flagKeys) => {
    return areFeaturesEnabled(flagKeys);
  }, [flagUpdateCounter]); // Re-memoize when flags update

  const getFeatureValueMemo = useCallback((flagKey, defaultValue) => {
    return getFeatureValue(flagKey, defaultValue);
  }, [flagUpdateCounter]); // Re-memoize when flags update

  // Set up subscription on mount
  useEffect(() => {
    // Subscribe to flag changes
    unsubscribeRef.current = subscribeToFlagChanges((changedFlags) => {
      if (watchedFlagsRef.current) {
        // Only update if watched flags changed
        const relevantChanges = watchedFlagsRef.current.some(flag => 
          Object.keys(changedFlags).includes(flag)
        );
        
        if (relevantChanges) {
          setFlagUpdateCounter(prev => prev + 1);
        }
      } else {
        // No specific flags watched, update on any change
        setFlagUpdateCounter(prev => prev + 1);
      }
    }, watchedFlagsRef.current);

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []); // Empty dependency array - subscription set up once on mount

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    isFeatureEnabled: isFeatureEnabledMemo,
    areFeaturesEnabled: areFeaturesEnabledMemo,
    getFeatureValue: getFeatureValueMemo,
    flagUpdateCounter
  };
};

export default useFeatureFlags;
